import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, MapPin, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/33659330/pexels-photo-33659330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithData } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { setError(""); setForgotSent(false); }, [mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "forgot") {
      try {
        await api.post("/auth/forgot-password", { email: form.email });
        setForgotSent(true);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      loginWithData(data.token, data.user);
      toast.success(mode === "register" ? "Conta criada com sucesso!" : "Bem-vindo de volta!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fafaf8]">
      {/* Left hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Lisboa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1b5e3f]/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 w-fit" data-testid="auth-logo">
            <span className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </span>
            <span className="font-head text-xl font-bold text-white">Brasileiros<span className="text-[#f59e0b]">.PT</span></span>
          </button>
          <div>
            <h2 className="font-head text-4xl xl:text-5xl font-bold text-white leading-tight">
              Sua rede brasileira<br />em Portugal, organizada.
            </h2>
            <p className="text-white/85 mt-5 max-w-md leading-relaxed">
              Centralize contatos por cidade, região, categoria e status. Mantenha o ritmo da sua comunidade.
            </p>
          </div>
          <p className="text-white/60 text-sm">© Brasileiros.PT · Lisboa, Portugal</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8" onClick={() => navigate("/")}>
            <span className="w-9 h-9 rounded-xl bg-[#1b5e3f] flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></span>
            <span className="font-head text-xl font-bold">Brasileiros<span className="text-[#f59e0b]">.PT</span></span>
          </div>

          <h1 className="font-head text-3xl sm:text-4xl font-bold text-[#1c1917]">
            {mode === "register" ? "Crie a sua conta" : mode === "forgot" ? "Recuperar senha" : "Bem-vindo de volta"}
          </h1>
          <p className="text-[#78716c] mt-2">
            {mode === "register" ? "Junte-se à rede de brasileiros em Portugal." : mode === "forgot" ? "Informe o seu e-mail e enviaremos um link para criar uma nova senha." : "Entre para acessar sua rede de clientes."}
          </p>

          {mode !== "forgot" && <button data-testid="google-login-btn" onClick={googleLogin} className="w-full mt-8 flex items-center justify-center gap-3 bg-white border border-[#e7e5e4] rounded-xl py-3.5 font-medium text-[#1c1917] hover:bg-[#fafaf8] transition-colors shadow-sm">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuar com Google
          </button>}

          {mode !== "forgot" && <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-[#e7e5e4]" />
            <span className="text-xs uppercase tracking-wider text-[#78716c] font-semibold">ou com e-mail</span>
            <div className="flex-1 h-px bg-[#e7e5e4]" />
          </div>}

          {mode === "forgot" && forgotSent ? (
            <div data-testid="forgot-success" className="mt-8 bg-[#f0f7f3] border border-[#1b5e3f]/20 rounded-xl p-5 text-sm text-[#1c1917] leading-relaxed">
              ✅ Se o e-mail <strong>{form.email}</strong> estiver cadastrado, enviamos um link de redefinição. Verifique a sua caixa de entrada (e a pasta de spam). O link é válido por 1 hora.
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium text-[#1c1917]">Nome completo</label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                  <input data-testid="auth-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Seu nome" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#1c1917]">E-mail</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                <input data-testid="auth-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@exemplo.com" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
              </div>
            </div>
            {mode !== "forgot" && <div>
              <label className="text-sm font-medium text-[#1c1917]">Senha</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716c]" />
                <input data-testid="auth-password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="w-full bg-white border border-[#e7e5e4] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all" />
              </div>
              {mode === "login" && (
                <div className="text-right mt-1.5">
                  <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-sm font-medium text-[#1b5e3f] hover:underline">
                    Esqueci minha senha
                  </button>
                </div>
              )}
            </div>}

            {error && <p data-testid="auth-error" className="text-sm text-[#ef4444] bg-red-50 rounded-lg px-3 py-2">{error}</p>}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === "register" ? "Criar conta" : mode === "forgot" ? "Enviar link de recuperação" : "Entrar"} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
          )}

          <p className="text-center text-sm text-[#78716c] mt-6">
            {mode === "forgot" ? (
              <button data-testid="back-to-login" onClick={() => setMode("login")} className="font-semibold text-[#1b5e3f] hover:underline">
                ← Voltar ao login
              </button>
            ) : (
              <>
                {mode === "register" ? "Já tem conta?" : "Ainda não tem conta?"}{" "}
                <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "register" ? "login" : "register")} className="font-semibold text-[#1b5e3f] hover:underline">
                  {mode === "register" ? "Entrar" : "Criar conta grátis"}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
